/*
 * Created on Mon Dec 27 2021
 *
 * Copyright (c) 2021 Cohart Inc - All Rights Reserved
 * Unauthorized copying of this file via any medium is strictly prohibited
 * Proprietary and confidential
 * Copyright terms written by Cohart Inc <support@cohart.co>, Mon Dec 27 2021
 */

import Head from 'next/head'

import { WEB_BASE_URL } from 'configs'
import { memo } from 'react'

export type MetadataProps = {
  title?: string
  twitterTitle?: string
  facebookTitle?: string
  description?: string
  twitterDescription?: string
  facebookDescription?: string
  thumbnail?: string
  thumbnailWidth?: number
  thumbnailHeight?: number
  thumbnailTwitter?: string
  thumbnailFacebook?: string
  url?: string
  keywords?: string
  metaRobots?: string
  structuredData?: string
  metaViewport?: string
  canonicalURL?: string
  preloadImageUrls?: string[]
  jsonLd?: { __html: string }
  jsonLdKey?: string
}

const DEFAULT_TITLE = 'The easiest way to discover, buy, and sell original art online'
const DEFAULT_DESCRIPTION =
  'Cohart is a vertical social marketplace for artists and art buyers. We help artists build their brands, find buyers, facilitate transactions, and manage their communities. For buyers, we offer algorithmic discovery tools, the ability to interact directly with artists, and a destination to showcase your collections.'

const Metadata = (props: MetadataProps) => {
  const {
    title,
    description,
    url,
    thumbnail,
    thumbnailFacebook,
    thumbnailTwitter,
    keywords,
    metaRobots,
    structuredData,
    metaViewport,
    twitterTitle,
    facebookTitle,
    twitterDescription,
    facebookDescription,
    canonicalURL,
    preloadImageUrls,
    jsonLd,
    jsonLdKey,
    thumbnailWidth,
    thumbnailHeight,
  } = props

  const displayTitle = title || DEFAULT_TITLE
  const displayDescription = description || DEFAULT_DESCRIPTION
  const displayUrl = `https://${WEB_BASE_URL}${url || '/'}`
  const defaultThumbnailFacebook = thumbnail || `https://${WEB_BASE_URL}/images/seo/thumbnail.png`
  const defaultThumbnailTwitter = thumbnail || `https://${WEB_BASE_URL}/images/seo/thumbnail-twitter.png`

  return (
    <Head>
      <title>{displayTitle}</title>
      <meta name="description" content={displayDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {metaRobots && <meta name="robots" content={metaRobots} />}
      {structuredData && <script type="application/ld+json">{structuredData}</script>}
      {metaViewport && <meta name="viewport" content={metaViewport} />}

      {/* <!-- Twitter Card data --> */}
      {/* <meta name="twitter:card" content="summary" /> */}
      <meta name="twitter:card" content="summary_large_image" key="twitter:card" />
      <meta name="twitter:site" content="@joincohart" />
      <meta name="twitter:title" content={twitterTitle || title} />
      <meta name="twitter:description" content={twitterDescription || description} />
      <meta name="twitter:creator" content="@joincohart" />
      <meta name="twitter:image" content={thumbnailTwitter || defaultThumbnailTwitter} />
      {/* <!-- Open Graph data --> */}
      <meta property="og:title" content={facebookTitle || title} />
      <meta property="og:description" content={facebookDescription || description} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={displayUrl} />
      <meta property="og:image" content={thumbnailFacebook || defaultThumbnailFacebook} />
      {thumbnailWidth && <meta property="og:image:width" content={thumbnailWidth.toString()} />}
      {thumbnailHeight && <meta property="og:image:height" content={thumbnailHeight.toString()} />}
      <meta property="og:site_name" content="cohart" />

      <link rel="canonical" href={canonicalURL || displayUrl} />

      {/* Preload images */}
      {preloadImageUrls &&
        preloadImageUrls.map((url, index) => <link key={index} rel="preload" href={url} as="image" />)}

      {/* JSON-LD */}
      {Boolean(jsonLd) && <script type="application/ld+json" dangerouslySetInnerHTML={jsonLd} key={jsonLdKey} />}
    </Head>
  )
}

export default memo(Metadata, () => true) // dont re-render
